import { isEmpty } from 'ramda';
import { isFunction } from 'ramda-extension';
import React, { useRef } from 'react';
import { useTranslation } from '../../contexts/translation';
import { Columns } from '../../pages/Reports/AssetResponsiblePerson/Columns';
import { SisDataGrid } from '../table';
import { IDataGrid } from './AssetsDataGrid';

interface Props extends IDataGrid {
  heading: string;
  filterVisibility: boolean;
  setFilterVisibility: (e: boolean) => void;
  filterChanged?: (hasFilter: boolean) => void;
  noTopPadding?: boolean;
}

export const ResponsiblePersonDataGrid = ({
  data,
  dataGridRef,
  resetFilter,
  heading,
  filterVisibility,
  setFilterVisibility,
  filterChanged,
  noTopPadding,
}: Props) => {
  const transFormName = 'ResponsiblePersonReport';
  const { translate } = useTranslation();
  const transFormReportBar = 'Reports.Bar';

  const hasFilterRef = useRef(false);
  const toolbarBaseItemsRef = useRef<any[] | null>(null);
  const badgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onToolbarPreparing = (e) => {
    if (!Array.isArray(e.toolbarOptions.items)) return;

    e.toolbarOptions.items.unshift({
      location: 'before',
      text: heading,
      visible: !filterVisibility,
    });

    e.toolbarOptions.items.unshift(
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'clear',
          type: 'info',
          text: translate!('Clear filter', transFormName),
          stylingMode: 'outlined',
          onClick: () => {
            if (resetFilter && isFunction(resetFilter)) {
              resetFilter();
            }
          },
        },
      },
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          visible: true,
          text: translate!(filterVisibility ? 'Hide' : 'Show', transFormReportBar),
          type: 'normal',
          onClick: () => setFilterVisibility(!filterVisibility),
        },
      },
    );

    toolbarBaseItemsRef.current = [...(e.toolbarOptions.items || [])];
  };

  const handleOptionChanged = (e: any) => {
    if (e.name === 'dataSource') return;
    const grid = e.component;
    if (badgeTimerRef.current !== null) clearTimeout(badgeTimerRef.current);
    badgeTimerRef.current = setTimeout(() => {
      badgeTimerRef.current = null;
      const combined = grid.getCombinedFilter();
      const hasFilterNow = !!combined;
      if (hasFilterRef.current !== hasFilterNow) {
        hasFilterRef.current = hasFilterNow;
        filterChanged && filterChanged(hasFilterNow);
      }
    }, 300);
  };

  return (
    <SisDataGrid
      enableXlsxExport
      exportName={'responsible-person-data'}
      show={!isEmpty(data)}
      dataGridRef={dataGridRef}
      data={data}
      keyExpr={'assetId'}
      transFormName={transFormName}
      columns={Columns}
      onToolbarPreparing={onToolbarPreparing}
      pageSizeChangeVisible={true}
      onOptionChanged={handleOptionChanged}
    />
  );
};
